import { Component, OnInit } from '@angular/core';
import { DateRange } from '@enums/enum';

import * as moment from 'moment';
import { Observable } from 'rxjs';
import { VehicleDistanceInterface } from '../../interfaces/vehicle-distance.interface';
import { DashboardService } from '../../services/dashboard.service';
import { VehicleDistanceQueryParams } from '../../interfaces/vehicle-distance-queryparams.interface';
import { Depot } from '@interfaces/depot.interface';
import { DepotService } from 'src/app/locations/services/depot.service';
import { tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { saveAs } from 'file-saver';
import * as ExcelJS from 'exceljs';
import { AngularMultiSelectOptions } from '@shared/constants/multiselect-dropdown.constants';
type AOA = any[][];

@Component({
    selector: 'app-dashboard-vehicle-distance',
    templateUrl: './dashboard-vehicle-distance.component.html',
    styleUrls: ['./dashboard-vehicle-distance.component.scss'],
})
export class DashboardVehicleDistanceComponent implements OnInit {
    public depots$: Observable<Depot[]>;
    public data$: Observable<VehicleDistanceInterface[]>;

    public dateRange: DateRange = DateRange.TWO_WEEKS;
    public dropdownList = [];
    public selectedItems = [];
    public dropdownSettings = {};
    private dataToExport: any[] = [];

    public columns = [
        { prop: 'code', name: this.translate.instant('Depot'), flexGrow: 3, sortable: true },
        { prop: 'date', name: this.translate.instant('Date'), flexGrow: 2, sortable: true },
        { prop: 'registration', name: this.translate.instant('Registration'), flexGrow: 4, sortable: true },
        { prop: 'routeNumber', name: this.translate.instant('Route number'), flexGrow: 2, sortable: true },
        { prop: 'shift', name: this.translate.instant('Shift'), flexGrow: 2, sortable: true },
        { prop: 'deliveries', name: this.translate.instant('Deliveries'), flexGrow: 2, sortable: true },
        { prop: 'travelTime', name: this.translate.instant('Travel time'), flexGrow: 2, sortable: true },
        { prop: 'parkingTime', name: this.translate.instant('Parking time'), flexGrow: 2, sortable: true },
        { prop: 'deliveryTime', name: this.translate.instant('Delivery time'), flexGrow: 2, sortable: true },
        { prop: 'departureDelay', name: this.translate.instant('Departure delay'), flexGrow: 3, sortable: true },
        { prop: 'km', name: this.translate.instant('Distance KM'), flexGrow: 2, sortable: true },
    ];

    public queryParams: VehicleDistanceQueryParams = {
        startDate: '',
        endDate: '',
        depotIds: [],
        shortRouteSkipThrehold: 5,
    };

    public tableMessages = {
        emptyMessage: this.translate.instant('No data to display'),
    };

    constructor(private readonly dashboardService: DashboardService, private readonly depotService: DepotService, private readonly translate: TranslateService) {}

    /**
     * Lifecycle hook that is called after data-bound properties are initialized.
     * Fetches the list of depots available to the user and initializes the dropdown list.
     */
    public ngOnInit() {
        this.depots$ = this.depotService.getDepotsForUser().pipe(
            tap((depots: Depot[]) => {
                this.initDropdownListValues(depots);
            })
        );
    }

    /**
     * Handles the date range change event.
     * Updates query parameters and triggers data loading.
     *
     * @param event - The event containing new date range and selected dates.
     */
    public changedDateRange(event) {
        this.queryParams.startDate = moment(event.dateStart).format('YYYY-MM-DD');
        this.queryParams.endDate = moment(event.dateEnd).format('YYYY-MM-DD');
        this.dateRange = event.dateRange;
        this.loadData();
    }

    /**
     * Loads vehicle distance data based on selected depot IDs and query parameters.
     * Populates the `dataToExport` array with translated headers and row data
     * to be used later in the Excel export.
     */
    private loadData() {
        this.queryParams.depotIds = this.selectedItems.map((a) => a.id);
        this.data$ = this.dashboardService.getVehiclesDistanceData(this.queryParams).pipe(
            tap((data: VehicleDistanceInterface[]) => {
                this.dataToExport = [];
                this.dataToExport.push([this.translate.instant('Depot'), this.translate.instant('Date'), this.translate.instant('Registration'), this.translate.instant('Route number'), this.translate.instant('Shift'), this.translate.instant('Deliveries'), this.translate.instant('Travel time'), this.translate.instant('Parking time'), this.translate.instant('Delivery time'), this.translate.instant('Departure delay'), this.translate.instant('Distance KM')]);

                data.forEach((r) => {
                    this.dataToExport.push([r.code, r.date, r.registration, r.routeNumber, r.shift, r.deliveries, r.travelTime, r.parkingTime, r.deliveryTime, r.departureDelay, r.km]);
                });
            })
        );
    }

    /**
     * Initializes the dropdown list with depots provided to the component.
     * Also sets the selectedItems and depotIds query parameter.
     *
     * @param depot - Array of depots available to the user.
     */
    public initDropdownListValues(depot: Depot[]): void {
        this.dropdownList = [];

        depot.forEach((d: Depot) => {
            this.dropdownList.push({ id: d.id, itemName: d.code });
        });

        this.selectedItems = this.queryParams.depotIds = this.dropdownList;
        this.dropdownSettings = AngularMultiSelectOptions;
    }

    /**
     * Exports the vehicle distance report as an Excel file (.xlsx).
     * Uses ExcelJS instead of xlsx to avoid vulnerable dependencies.
     * Applies bold formatting to the header row and adjusts column widths dynamically.
     */
    public async export(): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Vehicle Report');

        this.dataToExport.forEach((row, index) => {
            const newRow = worksheet.addRow(row);
            if (index === 0) {
                newRow.font = { bold: true };
            }
        });

        worksheet.columns.forEach((col, i) => {
            let maxLength = 10;
            this.dataToExport.forEach((row) => {
                const value = row[i];
                if (value && value.toString().length > maxLength) {
                    maxLength = value.toString().length;
                }
            });
            col.width = maxLength + 2;
        });

        const filename: string = `Report-${this.queryParams.startDate}-${this.queryParams.endDate}`.substring(0, 20) + '.xlsx';
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        saveAs(blob, filename);
    }
}
